import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../services/api';

export interface AccountingPeriod {
  id: number;
  accounting_period_id: number;
  accounting_period: {
    id: number;
    period_name: string;
    start_date: string;
    end_date: string;
    status: string;
  };
  status: 'OPEN' | 'LOCKED' | 'CLOSED';
  created_at: string;
}

interface PeriodContextType {
  selectedPeriod: AccountingPeriod | null;
  periods: AccountingPeriod[];
  currentPeriod: AccountingPeriod | null;
  setSelectedPeriod: (period: AccountingPeriod | null) => void;
  isLoading: boolean;
  isInitialized: boolean;
  error: Error | null;
}

const PeriodContext = createContext<PeriodContextType | undefined>(undefined);

const PERIOD_STORAGE_KEY = 'selected_period';
const PERIOD_STORAGE_TIMESTAMP_KEY = 'selected_period_timestamp';
const PERIOD_STORAGE_EXPIRY_DAYS = 7; // Reset to current period after 7 days

export const PeriodProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedPeriod, setSelectedPeriodState] = useState<AccountingPeriod | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Fetch periods from API
  const { data, isLoading, error } = useQuery({
    queryKey: ['reconciliation-periods'],
    queryFn: async () => {
      return await apiService.getReconciliationPeriods();
    },
  });

  const periods = data?.periods || [];

  // Find the default period (most recent period that has ended)
  const currentPeriod = React.useMemo(() => {
    if (!periods.length) return null;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Get all periods that have ended (end date is before today)
    const periodsBeforeToday = periods
      .filter(p => {
        const periodEnd = new Date(p.accounting_period.end_date);
        periodEnd.setHours(23, 59, 59, 999); // Set to end of day for proper comparison
        return periodEnd < today;
      })
      .sort((a, b) => new Date(b.accounting_period.end_date).getTime() - new Date(a.accounting_period.end_date).getTime());
    
    if (periodsBeforeToday.length > 0) {
      // Return the most recent period that has ended (regardless of status)
      return periodsBeforeToday[0];
    }
    
    // If no periods have ended yet, return the oldest period as fallback
    const sortedPeriods = [...periods]
      .sort((a, b) => new Date(a.accounting_period.end_date).getTime() - new Date(b.accounting_period.end_date).getTime());
    
    return sortedPeriods[0] || null;
  }, [periods]);

  // Check if stored period selection has expired
  const isStoredPeriodValid = () => {
    const timestamp = localStorage.getItem(PERIOD_STORAGE_TIMESTAMP_KEY);
    if (!timestamp) return false;
    
    const storedTime = new Date(timestamp);
    const now = new Date();
    const daysDiff = (now.getTime() - storedTime.getTime()) / (1000 * 3600 * 24);
    
    return daysDiff < PERIOD_STORAGE_EXPIRY_DAYS;
  };

  // Load selected period from localStorage on mount
  useEffect(() => {
    if (!periods.length || !currentPeriod) return;

    // Log for debugging
    // console.log('PeriodContext: Available periods:', periods.length);
    // console.log('PeriodContext: Current period:', currentPeriod?.accounting_period?.period_name);

    const storedPeriodId = localStorage.getItem(PERIOD_STORAGE_KEY);
    
    if (storedPeriodId && isStoredPeriodValid()) {
      try {
        const storedId = parseInt(storedPeriodId);
        // Verify the period still exists in our list
        const exists = periods.find(p => p.id === storedId);
        if (exists) {
          // console.log('PeriodContext: Using stored period:', exists.accounting_period?.period_name);
          setSelectedPeriodState(exists);
          setIsInitialized(true);
          return;
        }
      } catch (e) {
        // console.error('Error parsing stored period:', e);
      }
    }
    
    // Default to current period if no valid stored selection
    if (!selectedPeriod && currentPeriod) {
      // console.log('PeriodContext: Setting default period:', currentPeriod.accounting_period?.period_name);
      setSelectedPeriodState(currentPeriod);
      setIsInitialized(true);
    }
  }, [periods, currentPeriod]);

  // Update localStorage when selection changes
  const setSelectedPeriod = (period: AccountingPeriod | null) => {
    setSelectedPeriodState(period);
    if (period) {
      localStorage.setItem(PERIOD_STORAGE_KEY, period.id.toString());
      localStorage.setItem(PERIOD_STORAGE_TIMESTAMP_KEY, new Date().toISOString());
    } else {
      localStorage.removeItem(PERIOD_STORAGE_KEY);
      localStorage.removeItem(PERIOD_STORAGE_TIMESTAMP_KEY);
    }
  };

  return (
    <PeriodContext.Provider
      value={{
        selectedPeriod,
        periods,
        currentPeriod,
        setSelectedPeriod,
        isLoading,
        isInitialized,
        error: error as Error | null,
      }}
    >
      {children}
    </PeriodContext.Provider>
  );
};

export const usePeriod = () => {
  const context = useContext(PeriodContext);
  if (context === undefined) {
    throw new Error('usePeriod must be used within a PeriodProvider');
  }
  return context;
};